// Archivo de API para manejar las solicitudes de frontend
//
import axios from 'axios'
import { buildSafeApiErrorMetadata } from './apiErrorLogging'
import { resolveApiBaseUrlPolicy } from './apiBaseUrlPolicy'

// Obtener la URL base de las variables de entorno para el backend y los posibles servicios de terceros
const THIRD_PARTY_API_URL = process.env.REACT_APP_THIRD_PARTY_API_URL // Opcional, si es necesario
const apiBaseUrlPolicy = resolveApiBaseUrlPolicy()

// Crear la instancia principal de Axios para solicitudes API internas
const api = axios.create({
  // NOTA: Este baseURL ya incluye /api/v1
  baseURL: apiBaseUrlPolicy.baseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Content-Security-Policy':
      "default-src 'self'; img-src https:; script-src 'self'; style-src 'self';",
  },
})

// Crear una instancia secundaria de Axios para solicitudes de API de terceros (opcional)
const thirdPartyApi = axios.create({
  baseURL: THIRD_PARTY_API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

export const getAuthToken = () => {
  return (
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('authToken') ||
    null
  )
}

const storeAuthToken = (token, persist = 'local') => {
  if (!token) return
  if (persist === 'session') {
    sessionStorage.setItem('authToken', token)
    localStorage.removeItem('authToken')
    return
  }
  localStorage.setItem('authToken', token)
  sessionStorage.removeItem('authToken')
}

// 2. Me: obtener el payload (req.user) del accessToken en cookie
export const me = async (config = {}) => {
  const response = await api.get('/auth/me', config)
  return response.data.user
}

const parsePositiveInt = (value) => {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

const hasGlobalAdminRole = (user) => {
  if (!user || typeof user !== 'object') return false

  if (user.isGlobalAdmin === true) return true

  const directRole = typeof user.role === 'string' ? user.role : null
  if (directRole === 'globalAdmin') return true

  const roles = Array.isArray(user.roles) ? user.roles : []
  return roles.some((role) => {
    if (typeof role === 'string') return role === 'globalAdmin'
    if (!role || typeof role !== 'object') return false
    return role.type === 'globalAdmin' || role.name_role === 'globalAdmin'
  })
}

export const resolveLayoutsAuthContext = async ({ routeProductId } = {}) => {
  const authUser = await me()
  const isGlobalAdmin = hasGlobalAdminRole(authUser)

  const resolvedProductId =
    parsePositiveInt(routeProductId) ?? parsePositiveInt(authUser?.productId)

  if (!resolvedProductId) {
    throw new Error('No se pudo resolver productId efectivo para layouts')
  }

  if (!isGlobalAdmin) {
    return {
      isGlobalAdmin: false,
      productId: resolvedProductId,
      clientId: null,
      user: authUser,
    }
  }

  const productResponse = await api.get(`/products/${resolvedProductId}`, {
    params: { t: Date.now() },
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  })
  const rawProduct = productResponse?.data

  const product = Array.isArray(rawProduct)
    ? rawProduct[0]
    : rawProduct?.data && Array.isArray(rawProduct.data)
      ? rawProduct.data[0]
      : rawProduct?.data && typeof rawProduct.data === 'object'
        ? rawProduct.data
        : rawProduct

  const resolvedClientId = parsePositiveInt(
    product?.client_id ?? product?.clientId
  )

  if (!resolvedClientId) {
    throw new Error('No se pudo resolver clientId para globalAdmin')
  }

  return {
    isGlobalAdmin: true,
    productId: resolvedProductId,
    clientId: resolvedClientId,
    user: authUser,
  }
}

// Agregar un interceptor de solicitudes para inyectar tokens y encabezados de seguridad adicionales
/*
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const csrfToken = document.cookie.match(/CSRF-TOKEN=([^;]+)/)
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken[1]
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)
  */

// Añadir un interceptor de respuestas para el manejo centralizado de errores
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Manejar error 401 no autorizado (por ejemplo, token expirado)
    if (error.response && error.response.status === 401) {
      const token = getAuthToken()
      if (token) {
        console.warn('Authentication failed: token expired or invalid.')
      }
    }
    // Registrar errores para monitorización y depuración en producción
    console.error('API error', buildSafeApiErrorMetadata(error))
    return Promise.reject(error)
  }
)

// Funciones de ejemplo para solicitudes de API, modularizadas para llamadas seguras y reutilizables

// Obtener una lista de clientes
export const fetchClients = async () => {
  try {
    const response = await api.get('/clients')
    console.log('Clients response:', response.data)
    return response.data
  } catch (error) {
    console.error('Failed to fetch clients:', error)
    throw error
  }
}

// Autenticar el inicio de sesión del usuario
// @param {Object} credentials - Las credenciales de inicio de sesión
export const login = async (credentials, options = {}) => {
  try {
    const response = await api.post('/auth/login', credentials)
    const token = response?.data?.accessToken || response?.data?.token
    storeAuthToken(token, options?.persist === 'session' ? 'session' : 'local')
    return response.data
  } catch (error) {
    console.error('Login failed:', buildSafeApiErrorMetadata(error))
    throw error
  }
}

// Logout
export const logout = async () => {
  await api.post('/auth/logout')
}

export const setOperationalContext = async ({ clientId, productId }) => {
  const parsedClientId = parsePositiveInt(clientId)
  const parsedProductId = parsePositiveInt(productId)

  if (!parsedClientId || !parsedProductId) {
    throw new Error('clientId y productId deben ser enteros positivos')
  }

  const response = await api.post('/auth/operational-context', {
    clientId: parsedClientId,
    productId: parsedProductId,
  })

  const nextToken = response?.data?.accessToken || response?.data?.token
  const persistMode = sessionStorage.getItem('authToken') ? 'session' : 'local'
  storeAuthToken(nextToken, persistMode)

  return response.data
}

export const register = async () => {
  const error = new Error('Registro público no disponible en esta versión del CRM')
  error.code = 'PUBLIC_REGISTER_DISABLED'
  throw error
}

// Ejemplo de solicitud de API de terceros
// @param {string} endpoint - Punto final de API de terceros
// @param {Object} params - Parámetros de consulta
export const fetchThirdPartyData = async (endpoint, params) => {
  try {
    const response = await thirdPartyApi.get(endpoint, { params })
    return response.data
  } catch (error) {
    console.error('Third-party API error:', error)
    throw error
  }
}

// Esta sección es para para manejar las acciones de los botones de
// Creación
// Actualización
// Eliminación
export const createEntity = async (entityType, data) => {
  try {
    // baseURL ya incluye /api/v1, por lo tanto NO se antepone /api/v1 aquí.
    const response = await api.post(`/${entityType}`, data)
    return response.data
  } catch (error) {
    // Evitar romper si no hay response/data (por ejemplo, error de red)
    throw error && error.response && error.response.data
      ? error.response.data
      : error
  }
}

export const updateEntity = async (entityType, id, data) => {
  try {
    // baseURL ya incluye /api/v1, por lo tanto NO se antepone /api/v1 aquí.
    const response = await api.put(`/${entityType}/${id}`, data)
    return response.data
  } catch (error) {
    throw error && error.response && error.response.data
      ? error.response.data
      : error
  }
}

export const deleteEntity = async (entityType, id) => {
  try {
    // baseURL ya incluye /api/v1, por lo tanto NO se antepone /api/v1 aquí.
    const response = await api.delete(`/${entityType}/${id}`)
    return response.data
  } catch (error) {
    throw error && error.response && error.response.data
      ? error.response.data
      : error
  }
}

// Obtiene todos los layouts del usuario actual (y su producto),
// según la lógica del backend (req.user).
export const getLayouts = async (params = {}) => {
  const productId = parsePositiveInt(params?.productId)
  if (!productId) {
    throw new Error('No se pudo ejecutar GET /layouts sin productId')
  }

  if (params?.clientId !== undefined && params?.clientId !== null) {
    const clientId = parsePositiveInt(params.clientId)
    if (!clientId) {
      throw new Error('No se pudo ejecutar GET /layouts sin clientId válido')
    }
  }

  const response = await api.get('/layouts', { params })
  return response.data
}

export const getProductById = async (productId) => {
  const response = await api.get(`/products/${productId}`)
  return response.data
}

// Crea un layout nuevo.
// data debe incluir { name, config }.
export const createLayout = async (data, params = {}) => {
  const productId = parsePositiveInt(params?.productId)
  if (!productId) {
    throw new Error('No se pudo ejecutar POST /layouts/register sin productId')
  }

  if (params?.clientId !== undefined && params?.clientId !== null) {
    const clientId = parsePositiveInt(params.clientId)
    if (!clientId) {
      throw new Error(
        'No se pudo ejecutar POST /layouts/register sin clientId válido'
      )
    }
  }

  const response = await api.post('/layouts/register', data, { params })
  return response.data
}

// Actualiza un layout existente por ID.
// data puede incluir { name, config } según tu validador Joi.
export const updateLayout = async (id, data) => {
  const response = await api.put(`/layouts/${id}`, data)
  return response.data
}

// Elimina un layout existente por ID.
export const deleteLayout = async (id) => {
  const response = await api.delete(`/layouts/${id}`)
  return response.data
}

// BI endpoints
export const fetchBiProductsAllowed = async () => {
  const response = await api.get('/bi/products-allowed')
  return response.data
}

export const fetchBiSummary = async (productId) => {
  const response = await api.get('/bi/summary', { params: { productId } })
  return response.data
}

export const fetchBiActivityTrend = async (productId, from, to) => {
  const response = await api.get('/bi/activity-trend', {
    params: { productId, from, to },
  })
  return response.data
}

export const fetchBiTopCodes = async (productId, limit = 10) => {
  const response = await api.get('/bi/top-codes', {
    params: { productId, limit },
  })
  return response.data
}

export default api
